import React, { useState } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';
import { TEXT, errorText } from '../../config/listingPerformanceLabels';
import {
  usePerformanceThresholds, usePerformanceKeys, usePerformanceSubmissions,
  useSaveThresholds, useCreateKey, useRevokeKey
} from '../../hooks/useListingPerformance';
import { ThresholdChanges, Hint } from './parts';

/**
 * «أداء الوحدات» — settings: the judgement thresholds (founder edits, everyone reads, every change kept
 * with who/when/why — plan §2.9), the per-person send keys (founder only — D3) and the latest uploads.
 * A key is shown ONCE, right after it is created; the server never sends it again.
 */

const FIELDS = [
  'airbnbImpressionsMin', 'airbnbEntryRateMin', 'airbnbSimilarViewsMargin', 'airbnbMinPageViews',
  'occupancyTarget', 'occupancyAmberMargin', 'bookingcomCompMargin', 'bookingcomSearchAvailabilityMin'
];
// The similar-listings pair may stay EMPTY = its rule is off (layer-2 build D1); every other box needs a number.
const OPTIONAL = new Set(['airbnbSimilarViewsMargin', 'airbnbMinPageViews']);

const Thresholds = ({ isAdmin }) => {
  const { data, isPending, isError, error } = usePerformanceThresholds(true);
  const save = useSaveThresholds();
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState(null);
  const [failure, setFailure] = useState(null);

  if (isPending) return <div className="lp-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>;
  if (isError) return <div className="lp-error" role="alert">{errorText(error)}</div>;

  const values = draft || data.current;
  const setField = (f) => (e) => setDraft({ ...values, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setFailure(null);
    // An emptied box is NOT zero: 0 would silently switch a rule off. Only the optional pair may be empty
    // (sent as null = off); every other box must hold a number.
    const raw = FIELDS.map(f => [f, String(values[f] ?? '').trim()]);
    if (raw.some(([f, v]) => (v === '' ? !OPTIONAL.has(f) : !Number.isFinite(Number(v))))) {
      setFailure(TEXT.thresholdsIncomplete);
      return;
    }
    const body = { values: Object.fromEntries(raw.map(([f, v]) => [f, v === '' ? null : Number(v)])), reason };
    try {
      const res = await save.mutateAsync(body);
      setDraft(null);
      setReason('');
      setMessage(`${TEXT.savedFrom} ${formatDate(res.appliesFromWeekEnd)}`);
    } catch (err) {
      setFailure(errorText(err));
    }
  };

  return (
    <section className="lp-settings__block" aria-labelledby="lp-thr-title">
      <h3 id="lp-thr-title" className="lp-section-title">{TEXT.thresholdsTitle}</h3>
      <p className="lp-muted">{isAdmin ? TEXT.thresholdsHint : TEXT.thresholdsAdminOnly}</p>
      <Form onSubmit={submit} className="lp-form">
        {FIELDS.map(f => (
          <Form.Group key={f} controlId={`lp-thr-${f}`} className="lp-form__inline">
            <div>
              <Form.Label>{TEXT.thresholdLabels[f]}</Form.Label>
              {OPTIONAL.has(f) && <span id={`lp-thr-${f}-hint`} className="lp-form__hint">{TEXT.thresholdHints[f]}</span>}
            </div>
            <Form.Control
              type="number"
              step="any"
              dir="ltr"
              value={values[f] ?? ''}
              placeholder={OPTIONAL.has(f) ? TEXT.thresholdOff : undefined}
              aria-describedby={OPTIONAL.has(f) ? `lp-thr-${f}-hint` : undefined}
              onChange={setField(f)}
              disabled={!isAdmin}
              style={{ textAlign: 'start' }}
            />
          </Form.Group>
        ))}
        {isAdmin && (
          <>
            <Form.Group controlId="lp-thr-reason">
              <Form.Label>{TEXT.reason}</Form.Label>
              <Form.Control value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
            </Form.Group>
            {failure && <div className="lp-error" role="alert">{failure}</div>}
            {message && <div className="lp-ok" role="status">{message}</div>}
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending && <Spinner animation="border" size="sm" className="lp-spin" />} {TEXT.saveThresholds}
            </Button>
          </>
        )}
      </Form>

      <ThresholdHistory history={data.history} />
    </section>
  );
};

/** What a version changed against the one before it — in words, never six bare numbers. */
const changesOf = (v, older) => (older
  ? FIELDS.filter(f => v.values[f] !== older.values[f]).map(f => ({ field: f, from: older.values[f], to: v.values[f] }))
  : []);

/**
 * The thresholds' history, folded: one line says when they last changed and what; the full list is one
 * press away. Expected to be busy while the targets settle (founder, 15 Oct) — so it stays out of the way.
 */
const ThresholdHistory = ({ history = [] }) => {
  const [open, setOpen] = useState(false);
  const latest = history[0];
  if (!latest) return null;
  const line = (v, i) => (
    <>
      <span className="lp-muted">{formatDateTime(v.effectiveFrom)} — {v.changedBy || '—'}:</span>{' '}
      <ThresholdChanges changes={changesOf(v, history[i + 1])} />
      {v.reason && <span className="lp-muted"> — {v.reason}</span>}
    </>
  );
  return (
    <div className="lp-history-box">
      <p className="lp-history__latest">
        {latest.initial ? TEXT.noChangeYet : <><strong>{TEXT.lastChange}</strong> {line(latest, 0)}</>}
      </p>
      {history.length > 1 && (
        <Button variant="link" size="sm" className="lp-link" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          {open ? TEXT.hideHistory : <>{TEXT.showHistory} (<bdi className="lp-num">{history.length}</bdi>)</>}
        </Button>
      )}
      {open && (
        <ul className="lp-history">
          {history.map((v, i) => (
            <li key={v.version}>
              <span className="lp-history__version">{TEXT.version} {v.version}</span>
              {v.initial ? <span className="lp-muted">{TEXT.initialValues}</span> : line(v, i)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Keys = () => {
  const { data, isPending, isError, error } = usePerformanceKeys(true);
  const create = useCreateKey();
  const revoke = useRevokeKey();
  const [name, setName] = useState('');
  const [holder, setHolder] = useState('');
  const [fresh, setFresh] = useState(null);   // { key, name } — shown once
  const [copied, setCopied] = useState(false);
  const [failure, setFailure] = useState(null);
  const [confirming, setConfirming] = useState(null); // key id awaiting the second press

  if (isPending) return <div className="lp-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>;
  if (isError) return <div className="lp-error" role="alert">{errorText(error)}</div>;

  const submit = async (e) => {
    e.preventDefault();
    setFailure(null);
    try {
      const res = await create.mutateAsync({ name, userId: holder });
      setFresh({ key: res.key, name: res.keyInfo.name });
      setCopied(false);
      setName('');
    } catch (err) {
      setFailure(errorText(err));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fresh.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  // Two presses, in place — no browser pop-up. Revoking cannot be undone.
  const doRevoke = async (k) => {
    setFailure(null);
    try {
      await revoke.mutateAsync({ id: k.id });
      setConfirming(null);
    } catch (err) {
      setFailure(errorText(err));
    }
  };

  return (
    <section className="lp-settings__block" aria-labelledby="lp-keys-title">
      <h3 id="lp-keys-title" className="lp-section-title">{TEXT.keysTitle}</h3>
      <p className="lp-muted">{TEXT.keysHint}</p>

      {fresh && (
        <Alert variant="warning" className="lp-fresh-key">
          <p className="lp-fresh-key__lead">{TEXT.keyShownOnce}</p>
          <code dir="ltr" className="lp-fresh-key__value">{fresh.key}</code>
          <div className="lp-fresh-key__actions">
            <Button size="sm" variant="outline-dark" onClick={copy}>{copied ? TEXT.copied : TEXT.copy}</Button>
            <Button size="sm" variant="dark" onClick={() => setFresh(null)}>{TEXT.done}</Button>
          </div>
        </Alert>
      )}

      <Form onSubmit={submit} className="lp-form lp-form__row">
        <Form.Group controlId="lp-key-name">
          <Form.Label>{TEXT.keyName}</Form.Label>
          <Form.Control value={name} maxLength={60} placeholder={TEXT.keyNamePlaceholder} onChange={(e) => setName(e.target.value)} required />
        </Form.Group>
        <Form.Group controlId="lp-key-holder">
          <Form.Label>{TEXT.keyHolder}</Form.Label>
          <Form.Select value={holder} onChange={(e) => setHolder(e.target.value)} required>
            <option value="">—</option>
            {data.holders.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </Form.Select>
        </Form.Group>
        <Button type="submit" variant="primary" className="lp-form__submit" disabled={create.isPending || !name.trim() || !holder}>
          {create.isPending && <Spinner animation="border" size="sm" className="lp-spin" />} {TEXT.createKey}
        </Button>
      </Form>
      {failure && <div className="lp-error" role="alert">{failure}</div>}

      {!data.keys.length ? <p className="lp-empty">{TEXT.noKeys}</p> : (
        <table className="lp-table lp-keys">
          <thead>
            <tr>
              <th scope="col">{TEXT.keyName}</th>
              <th scope="col">{TEXT.keyHolder}</th>
              <th scope="col">{TEXT.created}</th>
              <th scope="col">{TEXT.lastUsed}</th>
              <th scope="col">{TEXT.uses}</th>
              <th scope="col" aria-label={TEXT.revoke} />
            </tr>
          </thead>
          <tbody>
            {data.keys.map(k => (
              <tr key={k.id} className={k.active ? '' : 'lp-keys__revoked'}>
                <th scope="row">{k.name} <code dir="ltr" className="lp-keys__prefix">{k.keyPrefix}…</code></th>
                <td data-label={TEXT.keyHolder}>{k.user?.name || '—'}</td>
                <td data-label={TEXT.created}>{formatDate(k.createdAt)}</td>
                <td data-label={TEXT.lastUsed}>{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : TEXT.neverUsed}</td>
                <td data-label={TEXT.uses}><bdi className="lp-num">{k.useCount}</bdi></td>
                <td>
                  {!k.active && <span className="lp-muted">{TEXT.revoked} {formatDate(k.revokedAt)}</span>}
                  {k.active && confirming !== k.id && (
                    <Button size="sm" variant="outline-danger" onClick={() => setConfirming(k.id)}>{TEXT.revoke}</Button>
                  )}
                  {k.active && confirming === k.id && (
                    <span className="lp-confirm" role="group" aria-label={TEXT.revokeConfirm}>
                      <span className="lp-confirm__text">{TEXT.revokeConfirm}</span>
                      <Button size="sm" variant="danger" onClick={() => doRevoke(k)} disabled={revoke.isPending}>{TEXT.revokeYes}</Button>
                      <Button size="sm" variant="link" className="lp-link" onClick={() => setConfirming(null)}>{TEXT.cancel}</Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

/** One delivery: when, who, what arrived — the routine's version kept small (it matters only when debugging). */
const UploadLine = ({ s }) => (
  <>
    <span>{formatDateTime(s.receivedAt)}</span>
    <span className="lp-muted"> — {s.sender?.name || '—'} — </span>
    <span>{s.summary?.text}</span>
    <span className="lp-muted lp-upload__version"> · <Hint text={TEXT.skillVersion} tap>
      <span className="lp-hint" tabIndex={0}>{TEXT.versionShort} <bdi dir="ltr">{s.skillVersion}</bdi></span>
    </Hint></span>
  </>
);

/**
 * «وصول الأرقام الأسبوعية» — every time the week's numbers were sent, folded: the latest delivery on one
 * line, the rest one press away (it fills up every Monday).
 */
const Uploads = () => {
  const { data, isPending, isError } = usePerformanceSubmissions(true);
  const [open, setOpen] = useState(false);
  if (isPending) return null;
  const list = data?.submissions || [];
  return (
    <section className="lp-settings__block" aria-labelledby="lp-up-title">
      <h3 id="lp-up-title" className="lp-section-title">{TEXT.uploads}</h3>
      <p className="lp-muted">{TEXT.uploadsHint}</p>
      {isError && <div className="lp-error" role="alert">{TEXT.uploadsError}</div>}
      {data && !data.enabled && <p className="lp-banner" role="status">{TEXT.receivingOff}</p>}
      {!isError && !list.length && <p className="lp-empty">{TEXT.noUploads}</p>}
      {!isError && list.length > 0 && (
        <div className="lp-history-box">
          <p className="lp-history__latest"><strong>{TEXT.lastUpload}</strong> <UploadLine s={list[0]} /></p>
          {list.length > 1 && (
            <Button variant="link" size="sm" className="lp-link" onClick={() => setOpen(o => !o)} aria-expanded={open}>
              {/* The server sends the latest ten — the button says so instead of promising «all». */}
              {open ? TEXT.hideUploads : <>{TEXT.showRecentUploads} <bdi className="lp-num">{list.length}</bdi></>}
            </Button>
          )}
          {open && (
            <ul className="lp-history">
              {list.map(s => <li key={s.id}><UploadLine s={s} /></li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

const PerformanceSettings = ({ isAdmin }) => (
  <div className="lp-settings">
    <Thresholds isAdmin={isAdmin} />
    {isAdmin && <Keys />}
    <Uploads />
  </div>
);

export default PerformanceSettings;
